import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef } from 'react'

import { usePlaybook } from './usePlaybook'
import type { BaseViewModel } from '@lib/store/playbook/BaseViewModel'
import { useSession } from '@lib/store/useStores'

type ViewModelConstructor<T extends BaseViewModel> = new (
    router: ReturnType<typeof useRouter>,
    searchParams: URLSearchParams,
    playbook: ReturnType<typeof usePlaybook>,
    session: ReturnType<typeof useSession>,
) => T

/**
 * Hook for managing view model instances in React components.
 * Provides automatic cleanup and consistent initialization patterns.
 *
 * @param ViewModelClass - The view model class constructor
 * @param deps - Optional dependency array for re-creating the view model
 * @param additionalArgs - Additional arguments to pass to the view model constructor
 * @returns The view model instance
 */
export function useViewModel<T extends BaseViewModel>(
    ViewModelClass: ViewModelConstructor<T>,
): T {
    const router = useRouter()
    const searchParams = useSearchParams()
    const playbook = usePlaybook()
    const sessionStore = useSession()

    // Create a stable URLSearchParams instance from the search params
    const urlSearchParams = useMemo(() => {
        return new URLSearchParams(searchParams.toString())
    }, [searchParams])

    // Create view model instance with memoization
    const viewModel = useMemo(() => {
        return new ViewModelClass(
            router,
            urlSearchParams,
            playbook,
            sessionStore,
        )
    }, [ViewModelClass, router, urlSearchParams])

    // Store the view model in a ref to maintain reference stability
    const viewModelRef = useRef<T>(viewModel)
    viewModelRef.current = viewModel

    // Cleanup on unmount
    useEffect(() => {
        const currentViewModel = viewModelRef.current

        return () => {
            if (
                currentViewModel &&
                typeof currentViewModel.dispose === 'function'
            ) {
                currentViewModel.dispose()
            }
        }
    }, [viewModel])

    return viewModel
}

/**
 * Alternative hook for cases where you need to manually control view model lifecycle
 * This is useful for complex scenarios where automatic cleanup isn't desired
 */
export function useViewModelRef<T extends BaseViewModel>(
    ViewModelClass: ViewModelConstructor<T>,
): { current: T | null; create: () => T; dispose: () => void } {
    const router = useRouter()
    const searchParams = useSearchParams()
    const playbook = usePlaybook()
    const sessionStore = useSession()
    const viewModelRef = useRef<T | null>(null)

    const urlSearchParams = useMemo(() => {
        return new URLSearchParams(searchParams.toString())
    }, [searchParams])

    const create = () => {
        if (
            viewModelRef.current &&
            typeof viewModelRef.current.dispose === 'function'
        ) {
            viewModelRef.current.dispose()
        }

        viewModelRef.current = new ViewModelClass(
            router,
            urlSearchParams,
            playbook,
            sessionStore,
        )

        return viewModelRef.current
    }

    const dispose = () => {
        if (
            viewModelRef.current &&
            typeof viewModelRef.current.dispose === 'function'
        ) {
            viewModelRef.current.dispose()
        }
        viewModelRef.current = null
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispose()
        }
    }, [])

    return {
        get current() {
            return viewModelRef.current
        },
        create,
        dispose,
    }
}
